import React from 'react';
import { Button as ReactButton } from 'reactstrap';
import styled, { css } from 'styled-components';
import { ReactComponent as IconCancel } from '../assets/images/icons/ico_cancel.svg';
import { ReactComponent as IconAceptar } from '../assets/images/icons/ico_aceptar.svg';
import { ReactComponent as IconEliminar } from '../assets/images/icons/ico_delete.svg';
import { ReactComponent as IconRegresar } from '../assets/images/icons/ico_arrow_circle.svg';
import { ReactComponent as IconDetalles } from '../assets/images/icons/ico_detail.svg';
import { PlusCircle } from 'react-feather';
import {Flex} from '@chakra-ui/react';

const ButtonColor = {
	primary: '#4A6080',
	primary_hover: '#2d3e74',
	secondary: '#C6CACE',
	secondary_hover: '#ADB1B7',
	lightgreen: '#72BF72',
	lightgreen_hover: '#72BF72',
	lightblue: '#4597EA',
	lightblue_hover: '#4597EA',
	asociar: '#72BF72',
	asociar_hover: '#72BF72',
	desasociar: '#4597EA',
	desasociar_hover: '#4597EA',
	default: '#014d98',
	default_hover: '#00326C',
	cancel: '#F5F6F7',
	hover_cancel: '#d3d3d3',
	light_finado: '#EBEAEFCC'
};

const Button = ({
	color,
	outline,
	disabled,
	type,
	name,
	children,
	style,
	primary,
	cancel,
	secondary,
	variant,

	onClick,
	...other
}) => {
	return (
		<ReactButton.Ripple
			color={color}
			disabled={disabled}
			backgroundcolor={
				primary
					? ButtonColor.primary
					: secondary
					? ButtonColor.secondary
					: variant
					? ButtonColor[variant]
					: cancel
					? ButtonColor.cancel
					: ButtonColor.default
			}
			hovercolor={
				primary
					? ButtonColor.primary_hover
					: secondary
					? ButtonColor.secondary_hover
					: variant
					? ButtonColor[`${variant}_hover`]
					: cancel
					? ButtonColor.hover_cancel
					: ButtonColor.default_hover
			}
			onClick={onClick}
			style={style}
			{...other}
		>
			{children}
		</ReactButton.Ripple>
	);
};

export const ButtonClasic = ({
	disabled,
	onClick,
	children,
	style,
	primary,
	cancel,
	secondary,
	variant,
	...other
}) => {
	const styles = {
		button: ({ backgroundColor, hoverColor }) => css`
			height: 2em;
			position: relative;
			font-weight: bold;
			color: white;
			background-color: ${backgroundColor};
			display: flex;
			font-size: 1em;
			font-family: 'Nutmeg', -apple-system, BlinkMacSystemFont, 'Segoe UI',
				'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans',
				'Helvetica Neue', sans-serif;
			border-radius: 0.5em;
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			outline: none;
			border: none;
			padding: 1.5em;
			cursor: pointer;
			font-family: 'Nutmeg Regular';
			font-weight: normal;
			:hover:enabled {
				background-color: ${hoverColor};
				transition: backgroundColor 0.3s ease-in-out;
			}
			&:visited,
			&:focus,
			&:active {
				background-color: ${backgroundColor};
			}
			:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
			.wait {
				cursor: wait;
			}
		`
	};

	const ActionButton = styled.button`
		${styles.button}
	`;

	return (
		<ActionButton
			disabled={disabled}
			backgroundColor={
				primary
					? ButtonColor.primary
					: secondary
					? ButtonColor.secondary
					: variant
					? ButtonColor[variant]
					: cancel
					? ButtonColor.cancel
					: ButtonColor.default
			}
			hoverColor={
				primary
					? ButtonColor.primary_hover
					: secondary
					? ButtonColor.secondary_hover
					: variant
					? ButtonColor[`${variant}_hover`]
					: cancel
					? ButtonColor.hover_cancel
					: ButtonColor.default_hover
			}
			onClick={onClick}
			style={style}
			{...other}
		>
			{children}
		</ActionButton>
	);
};

export const ButtonExamples = () => {
	return (
		<>
			<div>
				<ReactButton.Ripple color="flat-primary">Primary</ReactButton.Ripple>
				<ReactButton.Ripple color="flat-secondary">
					Secondary
				</ReactButton.Ripple>
				<ReactButton.Ripple color="flat-success">Success</ReactButton.Ripple>
				<ReactButton.Ripple color="flat-danger">Danger</ReactButton.Ripple>
				<ReactButton.Ripple color="flat-warning">Warning</ReactButton.Ripple>
			</div>
			<div>
				<ReactButton.Ripple color="gradient-primary">
					Primary
				</ReactButton.Ripple>
				<ReactButton.Ripple color="gradient-secondary">
					Secondary
				</ReactButton.Ripple>
				<ReactButton.Ripple color="gradient-success">
					Success
				</ReactButton.Ripple>
				<ReactButton.Ripple color="gradient-danger">Danger</ReactButton.Ripple>
				<ReactButton.Ripple color="gradient-warning">
					Warning
				</ReactButton.Ripple>
			</div>
			<div>
				<ReactButton.Ripple color="relief-primary">Primary</ReactButton.Ripple>
				<ReactButton.Ripple color="relief-secondary">
					Secondary
				</ReactButton.Ripple>
				<ReactButton.Ripple color="relief-success">Success</ReactButton.Ripple>
				<ReactButton.Ripple color="relief-danger">Danger</ReactButton.Ripple>
				<ReactButton.Ripple color="relief-warning">Warning</ReactButton.Ripple>
			</div>
		</>
	);
};

const iconsString = {
	detalles: <IconDetalles />,
	plus_circle: <PlusCircle />,
	regresar: <IconRegresar size={32} />
};

const IconButton = ({ icon, label, color, onClick, disabled, ...props }) => {
	return (
		<ReactButton.Ripple
			className="btn-icon"
			color={color}
			onClick={onClick}
			disabled={disabled}
			{...props}
		>
			<Flex direction='row' alignItems='center' width='100%'>
				{typeof icon === 'string' ? iconsString[icon] : icon}
				<span className="align-middle mx-auto icon-button-span">{label}</span>
			</Flex>
		</ReactButton.Ripple>
	);
};

const CancelButton = ({
	onClick,
	disabled,
	label,
	icon,
	color = 'cancel',
	...props
}) => {
	return (
		<IconButton
			onClick={onClick}
			disabled={disabled}
			label={label || 'Cancelar'}
			color={color}
			icon={icon || <IconCancel size={32} />}
			{...props}
		/>
	);
};

const BackButton = ({ onClick, disabled, label, icon, ...props }) => {
	return (
		<IconButton
			onClick={onClick}
			disabled={disabled}
			label={label || 'Cancelar'}
			color="cancel"
			icon={icon || 'regresar'}
			{...props}
		/>
	);
};

const ConfirmationButton = ({ onClick, disabled, label, icon, ...props }) => {
	return (
		<IconButton
			onClick={onClick}
			disabled={disabled}
			label={label || 'Guardar'}
			color="accept"
			icon={icon || <IconAceptar size={32} />}
			{...props}
		/>
	);
};

const FinadoButton = ({ onClick, disabled, label, icon, ...props }) => {
	return (
		<IconButton
			onClick={onClick}
			disabled={disabled}
			label={label || 'Marcar como fianado'}
			color="finado"
			{...props}
		/>
	);
};

const DeleteButton = ({ onClick, disabled, label, icon, ...props }) => {
	return (
		<IconButton
			onClick={onClick}
			disabled={disabled}
			label={label || 'Eliminar'}
			color="delete"
			icon={icon || <IconEliminar size={32} />}
			{...props}
		/>
	);
};

const DeclineButton = ({ onClick, disabled, label, icon, ...props }) => {
	return (
		<IconButton
			onClick={onClick}
			disabled={disabled}
			label={label || 'Eliminar'}
			color="delete"
			icon={icon || <IconCancel size={32} />}
			{...props}
		/>
	);
};

export {
	CancelButton,
	ConfirmationButton,
	DeleteButton,
	DeclineButton,
	BackButton,
	FinadoButton
};
export default Button;
